/**
 * 権限管理(管理者のみ)。ユーザー × 6権限のマトリクス。
 *
 * 役員・責任者・メンバーの線引きは社内協議中のため、ロールを増やさずに
 * 画面操作だけで追従できるようにする(要件定義書 3)。
 * 権限が未設定のユーザーは、ロールから導いた既定値を表示する。
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { fetchUsers, saveUserPermissions } from './masterRepo';
import {
  PERMISSION_DESCRIPTIONS,
  PERMISSION_KEYS,
  PERMISSION_LABELS,
  flowRoleFromLegacy,
  permissionsForUser,
  type PermissionKey,
  type Permissions,
  type User,
} from '@core/lib/types';

/** その人の実際の権限(承認者はすべての権限を持つ。2026-09-02 指示) */
function effectivePermissions(u: User): Permissions {
  return permissionsForUser(
    u.flow_role ?? flowRoleFromLegacy(u.role, u.is_accounting),
    u.permissions,
    u.role,
    u.is_accounting,
  );
}

/** 承認者は権限を個別に変えられない(すべて持つため) */
function isApprover(u: User): boolean {
  return (u.flow_role ?? flowRoleFromLegacy(u.role, u.is_accounting)) === 'approver';
}

export default function PermissionsTab() {
  const { user: me } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [draft, setDraft] = useState<Record<string, Permissions>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchUsers();
      setUsers(list);
      setDraft(
        Object.fromEntries(
          list.map((u) => [u.id, effectivePermissions(u)]),
        ),
      );
    } catch (e) {
      toast(e instanceof Error ? e.message : 'ユーザーを取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggle(userId: string, key: PermissionKey) {
    setDraft((prev) => {
      const current = prev[userId];
      if (!current) return prev;
      return { ...prev, [userId]: { ...current, [key]: !current[key] } };
    });
  }

  async function save(user: User) {
    const permissions = draft[user.id];
    if (!permissions) return;
    if (user.id === me?.id && !permissions.master_admin) {
      toast('自分のマスタ管理権限は外せません(操作できなくなるため)');
      return;
    }
    setBusyId(user.id);
    try {
      await saveUserPermissions(user.id, permissions);
      await load();
      toast(`${user.display_name} の権限を保存しました`);
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusyId(null);
    }
  }

  function isDirty(user: User): boolean {
    if (isApprover(user)) return false;
    const current = effectivePermissions(user);
    const next = draft[user.id];
    if (!next) return false;
    return PERMISSION_KEYS.some((k) => current[k] !== next[k]);
  }

  return (
    <>
      <div className="card">
        <h3>権限管理</h3>
        <p className="hint" style={{ marginBottom: 12 }}>
          ユーザーごとに権限をON/OFFします。ロールを増やさずに運用の線引きへ追従できます。
          権限を変更したユーザーは、次回の画面読み込みから反映されます(ログイン中の本人は再読み込みが必要)。
          役割が「承認者」の人はすべての権限を持つため、ここでは変更できません。
        </p>

        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : (
          <div>
            <table className="tx-table">
              <thead>
                <tr>
                  <th style={{ minWidth: 160 }}>ユーザー</th>
                  {PERMISSION_KEYS.map((key) => (
                    <th key={key} title={PERMISSION_DESCRIPTIONS[key]} style={{ textAlign: 'center' }}>
                      {PERMISSION_LABELS[key]}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      {user.display_name}
                      <div className="hint">
                        {user.email}
                        {isApprover(user) ? ' / 承認者(すべての権限)' : !user.permissions ? ' / 既定値(未設定)' : ''}
                        {!user.active && ' / 無効'}
                      </div>
                    </td>
                    {PERMISSION_KEYS.map((key) => (
                      <td key={key} style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={draft[user.id]?.[key] ?? false}
                          disabled={isApprover(user)}
                          onChange={() => toggle(user.id, key)}
                        />
                      </td>
                    ))}
                    <td>
                      <button
                        className="btn-sm-ok"
                        disabled={busyId === user.id || !isDirty(user)}
                        onClick={() => void save(user)}
                      >
                        保存
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h3>権限の意味</h3>
        <table>
          <tbody>
            {PERMISSION_KEYS.map((key) => (
              <tr key={key}>
                <td style={{ width: 160, fontWeight: 600 }}>{PERMISSION_LABELS[key]}</td>
                <td className="hint">{PERMISSION_DESCRIPTIONS[key]}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="hint" style={{ marginTop: 10 }}>
          承認は「承認」権限に加えて、対象事業部の責任者・所属者であるか、全社を見られる立場である必要があります。
          経費の確定は「確定」権限を持つ人だけが行えます(セキュリティルールでも同じ条件で制御しています)。
        </p>
      </div>
    </>
  );
}
